import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { SelectChangeEvent } from '@mui/material/Select'
import { a11yProps, TabPanel } from 'layouts/components/Tabs'
import { parseISO } from 'date-fns'

import {
  calendarHeaderToolbar,
  statesSelectOptions,
  TAB_DAILY,
  TAB_MONTHLY,
  TAB_SETTINGS,
  TAB_TIME_ALLOCATION,
  TAB_WEEKLY,
  timeZonesSelectOptions
} from './constants'
import TextFieldSkeleton from 'components/ui/Skeletons/TextFieldSkeleton'
import interactionPlugin from '@fullcalendar/interaction'
import timeGridPlugin from '@fullcalendar/timegrid'
import dayGridPlugin from '@fullcalendar/daygrid'
import SingleSelect from 'components/ui/Select/SingleSelect'
import FullCalendar from '@fullcalendar/react'
import TextField from 'components/ui/TextField'
import Tabs from '@mui/material/Tabs'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import * as Styles from 'layouts/PrivateLayout.styles'
import {
  locationsApi,
  useDeleteBlackoutMutation,
  useDeleteLocationMutation,
  useGetLocationByIdQuery,
  useUpdateLocationAvailabilityMutation,
  useUpdateLocationMutation
} from '../../../services/locations'
import { enqueueSnackbar } from 'notistack'
import LocationAvailabilityTable from './LocationAvailabilityTable'
import Button from '../../../components/ui/Button'
import { convertTo12HourFormat } from '../../../utils/convertTo12HourFormat'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material'
import DialogContentText from '@mui/material/DialogContentText'
import useCalendar from './hooks/useCalendar'
import WeekCalendar from './components/WeekCalendar'
import DailyCalendar from './components/DailyCalendar'
import { DatesSetArg } from '@fullcalendar/core'
import LocationTimeAllocationCalendar from './components/LocationTimeAllocationCalendar'

interface FormErrors {
  [key: string]: string
}

export interface IDateCalendar {
  start: Date
  end: Date
}

const LocationsEdit = ({ dateFrom }: { dateFrom?: IDateCalendar }) => {
  const calendarRef = useRef<any>(null)
  const { locationId = '' } = useParams()
  const [weeklyView, setWeeklyView] = useState<'order' | 'truck'>('order')
  const [dateCalendar, setDateCalendar] = useState<IDateCalendar | undefined>(
    dateFrom
  )
  const {
    data: results,
    isLoading,
    refetch: refetchLocation
  }: any = useGetLocationByIdQuery(locationId)

  // TODO: Handle proper pagination right now we are just grabbing everything
  const [bookingSlotsParams, setBookingSlotsParams] = useState({
    limit: 2000,
    page: 1,
    sort_by: 'date_start-desc',
    filter_by: ''
  })
  const { data: bookingSlots } = locationsApi.useGetLocationBookingSlotsQuery(
    { locationId, params: bookingSlotsParams },
    { skip: !locationId }
  )

  const { renderMonthlyEventContent } = useCalendar()

  const [createLocation] = locationsApi.useUpdateLocationMutation()
  const [updateLocation] = useUpdateLocationMutation()
  const [updateLocationAvailability] = useUpdateLocationAvailabilityMutation()
  const [events, setEvents]: any = useState([])

  const [deleteLocation] = useDeleteLocationMutation()
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

  const [deleteBlackout] = useDeleteBlackoutMutation()

  const today = new Date()
  const currentMonth = today.getMonth() + 1
  const currentYear = today.getFullYear()

  const handleDeleteBlackout = async (blackoutId: string) => {
    try {
      await deleteBlackout({ locationId, blackoutId }).unwrap()
      enqueueSnackbar('Blackout slot successfully deleted', {
        variant: 'success',
        autoHideDuration: 1000
      })
    } catch (error) {
      console.error('Failed to delete blackout slot:', error)
      enqueueSnackbar('Failed to delete blackout slot. Please try again.', {
        variant: 'error',
        autoHideDuration: 1000
      })
    }
  }

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }

  const handleConfirmDelete = async () => {
    try {
      await deleteLocation(locationId).unwrap()
      enqueueSnackbar('Location successfully deleted', {
        variant: 'success',
        autoHideDuration: 1000
      })
      navigate('/admin/locations') // Redirect to locations list
    } catch (error) {
      console.error('Failed to delete location:', error)
      enqueueSnackbar('Failed to delete location. Please try again.', {
        variant: 'error',
        autoHideDuration: 1000
      })
    }
    setOpenDeleteDialog(false)
  }

  const handleAvailabilityChange = (newAvailability: any) => {
    setLocationAvailability(
      newAvailability.map((avail: any, index: number) => ({
        ...avail,
        id: locationAvailability[index]?.id || avail.id // Maintain the original id if it exists
      }))
    )
  }

  const [locationOptionsTab, setLocationOptionsTab] = useState(
    dateFrom ? TAB_DAILY : TAB_MONTHLY
  )
  const [formData, setFormData] = useState({
    name: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    timezone: '',
    capacity: '',
    shopify_page_url: '',
    building_access_information: ''
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const navigate = useNavigate()
  const [openStatusDialog, setOpenStatusDialog] = useState(false)

  const [allBookingSlots, setAllBookingSlots] = useState<any[]>([])
  const [isLoadingAllSlots, setIsLoadingAllSlots] = useState(false)

  const [locationAvailability, setLocationAvailability]: any = useState([])

  const handleStatusChangeClick = () => {
    setOpenStatusDialog(true)
  }

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false)
  }

  const handleConfirmStatusChange = async () => {
    const newStatus = !results?.location?.status
    try {
      await updateLocation({
        id: locationId,
        body: {
          status: newStatus
        }
      }).unwrap()

      enqueueSnackbar(
        `Location successfully ${newStatus ? 'enabled' : 'disabled'}`,
        { variant: 'success', autoHideDuration: 1000 }
      )
      refetchLocation()
    } catch (error) {
      console.error('Failed to update location status:', error)
      enqueueSnackbar(
        `Failed to ${
          newStatus ? 'enable' : 'disable'
        } location. Please try again.`,
        { variant: 'error', autoHideDuration: 1000 }
      )
    }
    setOpenStatusDialog(false)
  }

  const [blackoutParams, setBlackoutParams]: any = useState({
    sort_by: 'date_start-desc'
  })

  const { data: blackoutsData, isLoading: isLoadingBlackouts } =
    locationsApi.useGetLocationBlackoutsQuery(
      {
        locationId,
        params: blackoutParams
      },
      { skip: !locationId }
    )

  const [blackouts, setBlackouts] = useState<any[]>([])

  useEffect(() => {
    if (blackoutsData && blackoutsData.blackouts) {
      const formattedBlackouts: any[] = blackoutsData.blackouts.map(
        (slot: any) => ({
          id: slot.id,
          date_start: slot.date_start.split('T')[0],
          date_end: slot.date_end.split('T')[0],
          working_time_start: convertTo12HourFormat(slot.working_time_start),
          working_time_end: convertTo12HourFormat(slot.working_time_end)
        })
      )
      setBlackouts(formattedBlackouts)
    }
  }, [blackoutsData])

  const handleBlackoutChange = (newBlackouts: any) => {
    setBlackouts(newBlackouts)
  }

  useEffect(() => {
    if (results?.location) {
      const timezoneOption = timeZonesSelectOptions.find((option) =>
        option.label.startsWith(results.location.timezone)
      )

      setFormData({
        name: results.location.name,
        address_1: results.location.address_1,
        address_2: results.location.address_2 || '',
        city: results.location.city,
        state: results.location.state,
        zip_code: results.location.zip_code,
        timezone: timezoneOption ? timezoneOption.id : '',
        capacity: '1',
        shopify_page_url: results.location.shopify_page_url || '',
        building_access_information:
          results.location.building_access_information || ''
      })

      if (results.location.location_availabilities) {
        const formattedAvailability =
          results.location.location_availabilities.map((avail: any) => ({
            id: avail.id,
            day: avail.day_of_week_name,
            duration: avail.duration,
            startTime: avail.working_time_start.slice(0, 5), // Extract HH:MM from HH:MM:SS
            endTime: avail.working_time_end.slice(0, 5), // Extract HH:MM from HH:MM:SS
            isActive: avail.is_active
          }))
        setLocationAvailability(formattedAvailability)
      }
    }
  }, [results?.location])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target
    setFormData((prev) => ({ ...prev, [id]: value }))
  }

  const handleRichTextChange = (content: string) => {
    setFormData((prev) => ({ ...prev, building_access_information: content }))
  }

  const handleSelectChange = (event: SelectChangeEvent<any>) => {
    const { name, value } = event.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setLocationOptionsTab(newValue)

    if (newValue === TAB_MONTHLY) {
      const calendarApi = calendarRef?.current?.getApi()
      calendarApi?.changeView('dayGridMonth')
    }

    if (newValue === TAB_WEEKLY) {
      const calendarApi = calendarRef?.current?.getApi()
      calendarApi?.changeView('timeGridWeek')
    }

    if (newValue === TAB_DAILY) {
      const calendarApi = calendarRef?.current?.getApi()
      calendarApi?.changeView('timeGridDay')

      const dayStart = dateCalendar?.start || new Date()
      const dayEnd = dateCalendar?.start
        ? new Date(dateCalendar.start)
        : new Date()
      dayEnd.setDate(dayEnd.getDate() + 1)
      handleDateChange({
        start: dayStart,
        end: dayEnd,
        startStr: dayStart.toDateString(),
        endStr: dayEnd.toDateString(),
        timeZone: dayStart.getTimezoneOffset().toString(),
        view: 'dayGridDay' as any
      })
    }
  }

  const validateForm = (formData: any): FormErrors => {
    const errors: FormErrors = {}
    if (!formData.name?.trim()) errors.name = 'Location name is required'
    if (!formData.address_1?.trim()) errors.address_1 = 'Address is required'
    if (!formData.zip_code?.trim()) errors.zip_code = 'ZIP code is required'
    if (formData.zip_code && !/^\d{5}(-\d{4})?$/.test(formData.zip_code))
      errors.zip_code = 'Invalid ZIP code'
    if (!formData.city?.trim()) errors.city = 'City is required'
    if (!formData.state) errors.state = 'State is required'
    if (!formData.timezone) errors.timezone = 'Time zone is required'
    if (
      formData.shopify_page_url &&
      !/^https?:\/\/.+/.test(formData.shopify_page_url)
    ) {
      errors.shopify_page_url = 'Invalid URL format'
    }
    return errors
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const formErrors = validateForm(formData)
    if (Object.keys(formErrors).length > 0) {
      console.log('ERRORS FOUND:', formErrors)
      setErrors(formErrors)
      return
    }

    const selectedTimezone = timeZonesSelectOptions.find(
      (option) => option.id === formData.timezone
    )

    if (!selectedTimezone) {
      console.error('Invalid timezone selected')
      setErrors({ timezone: 'Please select a valid timezone' })
      return
    }

    const updateLocationData = {
      id: locationId,
      body: {
        name: formData.name,
        address_1: formData.address_1,
        address_2: formData.address_2 || '',
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        country: 'US',
        timezone: selectedTimezone.label.split(' ')[0],
        capacity: 1,
        shopify_location_id: null,
        shopify_page_url: formData.shopify_page_url,
        building_access_information: formData.building_access_information
      }
    }

    try {
      // Update location details
      await updateLocation(updateLocationData).unwrap()

      // Update each availability separately
      const availabilityUpdates = locationAvailability.map((avail: any) =>
        updateLocationAvailability({
          locationId,
          availabilityId: avail.id,
          body: {
            working_time_start: avail.startTime, // Already in HH:MM format
            working_time_end: avail.endTime, // Already in HH:MM format
            duration: avail.duration,
            is_active: avail.isActive
          }
        }).unwrap()
      )

      // Wait for all availability updates to complete
      await Promise.all(availabilityUpdates)

      console.log('Location and availabilities updated successfully')
      enqueueSnackbar('Successfully edited the location and availabilities', {
        variant: 'success',
        autoHideDuration: 1000
      })

      await refetchLocation()

      setBookingSlotsParams((prev) => ({ ...prev }))

      setErrors({})
    } catch (error) {
      console.error('Failed to update location or availabilities:', error)
      setErrors({
        submit: 'Failed to update location or availabilities. Please try again.'
      })
      enqueueSnackbar(
        'Failed to update location or availabilities. Please try again.',
        { variant: 'error', autoHideDuration: 1000 }
      )
    }
  }

  const fetchAllBookingSlots = useCallback(async () => {
    setIsLoadingAllSlots(true)
    let currentPage = 1
    let hasNextPage = true
    let allSlots: any[] = []

    // while (hasNextPage) {
    //     try {
    //         const resultAction: any =
    //             locationsApi.endpoints.getLocationBookingSlots.initiate({
    //                 locationId,
    //                 params: {...bookingSlotsParams, page: currentPage}
    //             })
    //
    //         if ('data' in resultAction) {
    //             const result = resultAction.data
    //             console.log(result && result.booking_slots)
    //             if (result && result.booking_slots) {
    //                 allSlots = [...allSlots, ...result.booking_slots]
    //                 hasNextPage = result.next_page !== null
    //                 currentPage++
    //             } else {
    //                 hasNextPage = false
    //             }
    //         } else {
    //             console.error('Error fetching booking slots:', resultAction.error)
    //             hasNextPage = false
    //         }
    //     } catch (error) {
    //         console.error('Error fetching booking slots:', error)
    //         enqueueSnackbar('Failed to fetch all booking slots', {
    //             variant: 'error',
    //             autoHideDuration: 1000
    //         })
    //         hasNextPage = false
    //     }
    // }

    setAllBookingSlots(allSlots)
    setIsLoadingAllSlots(false)
  }, [locationId, bookingSlotsParams])

  const handleBlackoutButtonClick = () => {
    navigate('/admin/locations/blackouts', {
      state: { locations: [locationId] }
    })
  }

  const handleEventClick = (clickInfo: any) => {
    setLocationOptionsTab(2)

    const dayEnd = new Date(clickInfo?.event?._instance?.range.start)
    dayEnd.setDate(clickInfo?.event?._instance?.range.start.getDate() + 1)
    handleDateChange({
      start: clickInfo?.event?._instance?.range.start,
      end: dayEnd,
      startStr: clickInfo?.event?._instance?.range.startStr,
      endStr: clickInfo?.event?._instance?.range.endStr,
      timeZone: clickInfo?.event?._instance?.range.time,
      view: clickInfo?.event?._instance?.range.view
    })
  }

  const handleDateClick = (arg: any) => {
    console.log('🚀 ~ handleDateClick ~ arg:', arg)
  }

  const handleDateChange = useCallback((dateInfo: DatesSetArg) => {
    const startDate = dateInfo.start
    const endDate = dateInfo.end

    const startDateStringISO = startDate.toISOString().split('T')[0]
    const endDateStringISO = endDate.toISOString().split('T')[0]

    setDateCalendar({
      start: startDate,
      end: endDate
    })
    setBookingSlotsParams((prev) => ({
      ...prev,
      page: 1,
      filter_by: `date_start:">=${startDateStringISO}" date_start:"<${endDateStringISO}"`
    }))
  }, [])

  useEffect(() => {
    if (dateFrom) {
      const dayStart = new Date(dateFrom.start)
      const dayEnd = new Date(dateFrom.end)
      dayEnd.setDate(dayEnd.getDate() + 1)

      handleDateChange({
        start: dayStart,
        end: dayEnd,
        startStr: dayStart.toDateString(),
        endStr: dayEnd.toDateString(),
        timeZone: dayStart.getTimezoneOffset().toString(),
        view: 'dayGridDay' as any
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom])

  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi()
      const currentDate = calendarApi.getDate()

      if (!dateCalendar) {
        setDateCalendar({
          start: currentDate,
          end: currentDate
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (bookingSlots && bookingSlots.booking_slots) {
      const timeZone = 'America/New_York' // Florida timezone

      const formattedEvents = bookingSlots.booking_slots.map((slot) => {
        // Parse date in UTC
        const utcDateStart = parseISO(slot.date_start)
        const utcDateEnd = parseISO(slot.date_end)

        // Extract the date part without converting time zones
        const formattedDateStart = utcDateStart.toISOString().split('T')[0] // YYYY-MM-DD
        const formattedDateEnd = utcDateEnd.toISOString().split('T')[0] // YYYY-MM-DD
        return {
          id: slot?.id,
          title: `#${slot?.order?.order_number} - ${slot?.customer?.first_name} - ${slot?.customer?.last_name}`,
          start: formattedDateStart,
          end: formattedDateEnd,
          name: slot?.customer?.name,
          orderNumber: slot?.order?.order_number,
          orderId: slot?.order?.id,
          source_order_id: slot?.order?.source_order_id,
          truckAssignments: slot?.truck_assignments,
          assignedTrucksAtLocation: slot?.assignedTrucksAtLocation,
          overrides: slot?.overrides,
          locationAvailabilities: slot?.locationAvailabilities,
          status: slot?.status,
          textColor: slot?.status === 'completed' ? '#1B560A' : '#003B71',
          backgroundColor: slot?.status === 'completed' ? '#EEF8E3' : '#E3EFF8',
          borderColor: slot?.status === 'completed' ? '#EEF8E3' : '#E3EFF8',
          classNames: slot?.status === 'completed' ? 'fc-event-completed' : ''
        }
      })
      setEvents(formattedEvents)
    }
  }, [bookingSlots])

  useEffect(() => {
    fetchAllBookingSlots()
  }, [bookingSlotsParams, fetchAllBookingSlots])

  return (
    <div>
      <Styles.PageTitleContainer>
        <div>{results?.location ? results?.location?.name : ''}</div>
      </Styles.PageTitleContainer>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={locationOptionsTab}
          onChange={handleChangeTab}
          aria-label="Location tabs"
        >
          <Tab label="Monthly" {...a11yProps(TAB_MONTHLY)} />
          <Tab label="Weekly" {...a11yProps(TAB_WEEKLY)} />
          <Tab label="Daily" {...a11yProps(TAB_DAILY)} />
          <Tab label="Settings" {...a11yProps(TAB_SETTINGS)} />
          <Tab label="Time Allocation" {...a11yProps(TAB_TIME_ALLOCATION)} />
        </Tabs>
      </Box>
      <TabPanel value={locationOptionsTab} index={TAB_MONTHLY}>
        <div>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={dateCalendar?.start}
            headerToolbar={calendarHeaderToolbar}
            events={events}
            eventContent={renderMonthlyEventContent}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            dayMaxEvents={4}
            datesSet={handleDateChange}
            displayEventTime={false}
            eventDisplay="block"
          />
        </div>
      </TabPanel>
      <TabPanel value={locationOptionsTab} index={TAB_WEEKLY}>
        <div>
          <WeekCalendar
            locationId={locationId}
            events={events}
            handleDateChange={handleDateChange}
            handleEventClick={handleEventClick}
            weeklyView={weeklyView}
            setWeeklyView={setWeeklyView}
            initialDate={dateCalendar}
            currentMonth={currentMonth}
            currentYear={currentYear}
          />
        </div>
      </TabPanel>
      <TabPanel value={locationOptionsTab} index={TAB_DAILY}>
        <div>
          <DailyCalendar
            locationId={locationId}
            dailyDate={dateCalendar}
            events={events}
            handleDateChange={handleDateChange}
            weeklyView={weeklyView}
            setWeeklyView={setWeeklyView}
            currentMonth={currentMonth}
            currentYear={currentYear}
          />
        </div>
      </TabPanel>
      <TabPanel value={locationOptionsTab} index={TAB_SETTINGS}>
        <form onSubmit={handleSubmit}>
          <Styles.FormColumns border-type="border">
            <Styles.FormColumnLeft>Location Name*</Styles.FormColumnLeft>
            <Styles.FormColumnRight>
              <TextField
                id="name"
                required
                cc-width="full"
                label="Location name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Styles.FormColumnRight>
          </Styles.FormColumns>

          <Styles.FormColumns border-type="border">
            <Styles.FormColumnLeft>Address*</Styles.FormColumnLeft>
            <Styles.FormColumnRight>
              <TextField
                id="address_1"
                label="Address"
                required
                cc-width="full"
                value={formData.address_1}
                onChange={handleInputChange}
                error={!!errors.address_1}
                helperText={errors.address_1}
              />
              <TextField
                id="address_2"
                label="Address 2"
                cc-width="full"
                value={formData.address_2}
                onChange={handleInputChange}
              />
              <Styles.SectionThreeColumns>
                <TextField
                  id="zip_code"
                  label="ZIP"
                  type="text"
                  required
                  cc-width="full"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  error={!!errors.zip_code}
                  helperText={errors.zip_code}
                />
                <TextField
                  id="city"
                  label="City"
                  required
                  cc-width="full"
                  value={formData.city}
                  onChange={handleInputChange}
                  error={!!errors.city}
                  helperText={errors.city}
                />
                <SingleSelect
                  id="state"
                  name="state"
                  label={'State'}
                  value={formData.state}
                  onChange={handleSelectChange}
                  required
                  options={[
                    { id: '', label: 'Select State' },
                    ...statesSelectOptions
                  ]}
                  error={!!errors.state}
                />
              </Styles.SectionThreeColumns>
            </Styles.FormColumnRight>
          </Styles.FormColumns>

          <Styles.FormColumns border-type="border">
            <Styles.FormColumnLeft>Time Zone*</Styles.FormColumnLeft>
            <Styles.FormColumnRight>
              <TextFieldSkeleton isLoading={isLoading}>
                <SingleSelect
                  id="timezone"
                  name="timezone"
                  label={'Select time zone'}
                  value={formData.timezone}
                  onChange={handleSelectChange}
                  required
                  options={[
                    { id: '', label: 'Select Time Zone' },
                    ...timeZonesSelectOptions
                  ]}
                  error={!!errors.timezone}
                />
              </TextFieldSkeleton>
            </Styles.FormColumnRight>
          </Styles.FormColumns>

          {/*<Styles.FormColumns border-type="border">*/}
          {/*    <Styles.FormColumnLeft>Capacity</Styles.FormColumnLeft>*/}
          {/*    <Styles.FormColumnRight>*/}
          {/*        <TextField*/}
          {/*            id="capacity"*/}
          {/*            type="number"*/}
          {/*            cc-width="full"*/}
          {/*            value={formData.capacity}*/}
          {/*            onChange={handleInputChange}*/}
          {/*            error={!!errors.capacity}*/}
          {/*            helperText={errors.capacity}*/}
          {/*        />*/}
          {/*    </Styles.FormColumnRight>*/}
          {/*</Styles.FormColumns>*/}

          {errors.submit && (
            <Styles.FormColumns>
              <Styles.FormColumnLeft />
              <Styles.FormColumnRight>
                <div style={{ color: 'red' }}>{errors.submit}</div>
              </Styles.FormColumnRight>
            </Styles.FormColumns>
          )}

          <div style={{ paddingTop: 20 }}></div>
          <Styles.PageTitleContainer>
            <div>Location Availability</div>
          </Styles.PageTitleContainer>
          <LocationAvailabilityTable
            availability={locationAvailability}
            onAvailabilityChange={handleAvailabilityChange}
          />

          <div style={{ paddingTop: 20 }}></div>
          {/*<Styles.PageTitleContainer>*/}
          {/*  <div>Blackout Slots</div>*/}
          {/*  <Button*/}
          {/*    id="addLocationButton"*/}
          {/*    loading={isLoading}*/}
          {/*    onClick={() => handleBlackoutButtonClick()}*/}
          {/*  >*/}
          {/*    + Add Blackout Slots*/}
          {/*  </Button>*/}
          {/*</Styles.PageTitleContainer>*/}
          {/*{isLoadingBlackouts ? (*/}
          {/*  <p>Loading blackout slots...</p>*/}
          {/*) : (*/}
          {/*  <BlackoutSlotTable*/}
          {/*    availability={blackouts}*/}
          {/*    onAvailabilityChange={handleBlackoutChange}*/}
          {/*    onDeleteBlackout={handleDeleteBlackout}*/}
          {/*  />*/}
          {/*)}*/}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingTop: 40
            }}
          >
            <Styles.FormColumnLeft>
              <Button
                id="changeStatusButton"
                onClick={handleStatusChangeClick}
                cc-color={results?.location?.status ? 'red' : 'purple'}
              >
                {results?.location?.status ? 'Disable' : 'Enable'} Location
              </Button>
            </Styles.FormColumnLeft>
            <Styles.FormColumnRight>
              <Styles.FormColumnsFooterButtons>
                <Link to={'/admin/locations'}>
                  <Button>Cancel</Button>
                </Link>
                <Button
                  id="addLocationButton"
                  type="submit"
                  loading={isLoading}
                  cc-color="purple"
                >
                  Save
                </Button>
              </Styles.FormColumnsFooterButtons>
            </Styles.FormColumnRight>
          </div>
        </form>
      </TabPanel>
      <TabPanel value={locationOptionsTab} index={TAB_TIME_ALLOCATION}>
        <div>
          <LocationTimeAllocationCalendar
            locationId={locationId}
            currentMonth={currentMonth}
            currentYear={currentYear}
          />
        </div>
      </TabPanel>
      <Dialog
        open={openStatusDialog}
        onClose={handleCloseStatusDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirm'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to{' '}
            {results?.location?.status ? 'disable' : 'enable'} this location?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmStatusChange}
            cc-color={results?.location?.status ? 'red' : 'purple'}
            autoFocus
          >
            {results?.location?.status ? 'Disable' : 'Enable'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default LocationsEdit
